import { AllSameKeyValuesEnforcer, getSet } from '../helpers/helpers';
import { Program, EntityRequest } from './programs.data';
import { FilterGroup } from './filters.data';
import { JoiEvent, Ranking } from './events.data';
import { addExcludedEntityTemplateKeys, addPublicEntityKeys, EntityBase, entityProps } from './entity.shared.data';
import { Venue } from './events.data';
import { JoiFile } from './files.model';

interface ExhibitorTabs { info, details, quickNotes, configBrief, preview, briefInfo, contact, tasks, assets, schedule }
export const exhibitorTabs: AllSameKeyValuesEnforcer<ExhibitorTabs> = { info: 'info', details: 'details', quickNotes: 'quickNotes', configBrief: 'configBrief', preview: 'preview', briefInfo: 'briefInfo', contact: 'contact', tasks: 'tasks', assets: 'assets',
schedule: 'schedule' }

interface ExhibitorStandTabs { details, assets, requests }
export const exhibitorStandTabs: AllSameKeyValuesEnforcer<ExhibitorStandTabs> = { details: 'details', assets: 'assets', requests: 'requests'}

interface ExhibitorDisplayTypes { readOnly, widePod, narrowPod, staticCard }
export const exhibitorDisplayTypes: AllSameKeyValuesEnforcer<ExhibitorDisplayTypes> = { readOnly: 'readOnly', widePod: 'widePod', narrowPod: 'narrowPod', staticCard: 'staticCard' }


interface StandBase { // Shared 
  standNotes?: string
  standTypeId?: string
  programId?: string // Room / location
  // programIndex?: number // not an actual index, but a relative one (since removing an exhibitor won't change the indices stored)
}
export interface Exhibitor extends EntityBase {
  workforceContactEmail?: string
  contactEmail?: string
  contactNumber?: string
  description?: string
  callToAction?: string
  callToActionUrl?: string
  hideLogo?: boolean
  files?: JoiFile[]
  detailsConfirmed?: boolean
  showEditProfileLinkInBrief?: boolean
  locationText?: string // for use in program publish => exhibitor profile, only
  showAssetsLinkInBrief?: boolean
}

export type ExhibitorWithStand = StandBase & Exhibitor & { standNumber }// This is ONLY for old data to transform
export interface ExhibitorStand extends StandBase {
  id: string
  name: string
  exhibitorId?: string
  programIndex?: number
  exhibitorIndex?: number
  assetIds: string[]
  requests: EntityRequest[]
}
const exhibitorOnlyProps: AllSameKeyValuesEnforcer<Omit<Exhibitor, keyof EntityBase>> = {
  workforceContactEmail: 'workforceContactEmail', files: 'files', contactEmail: 'contactEmail', contactNumber: 'contactNumber', 
  description: 'description', callToAction: 'callToAction', callToActionUrl: 'callToActionUrl', hideLogo: 'hideLogo', 
  detailsConfirmed: 'detailsConfirmed', showEditProfileLinkInBrief: 'showEditProfileLinkInBrief', locationText: 'locationText', 
  showAssetsLinkInBrief: 'showAssetsLinkInBrief' 
}

export const exhibitorProps: AllSameKeyValuesEnforcer<Exhibitor> = { ...entityProps, ...exhibitorOnlyProps }
export const excludedTemplateExhibitorProps: Set<keyof Exhibitor> = new Set(['hideLabels', 'hideLogo', 'detailsConfirmed', 'showEditProfileLinkInBrief', 'locationText', 'showAssetsLinkInBrief' ])
addExcludedEntityTemplateKeys(excludedTemplateExhibitorProps)

export const publicExhibitorKeys: Set<keyof Exhibitor> = new Set(['description', 'callToAction', 'callToActionUrl', 'workforceContactEmail', 'showEditProfileLinkInBrief', 'files', 'contactName', 'contactNumber', 'contactEmail' ])
addPublicEntityKeys(publicExhibitorKeys)

export interface ProgramsExhibitors {
  programList: Program[]
  exhibitorList: Exhibitor[]
}
export interface ProgramsExhibitorStands {
  programList: Program[]
  exhibitorList: Exhibitor[]
  exhibitorStandList: ExhibitorStand[]
}

export const filterExhibitorStands = (filters: FilterGroup, programs: { [pId:string]: Program }, exhibitorStandList: ExhibitorStand[], exhibitors: { [eId: string]: Exhibitor }): ProgramsExhibitorStands => {
  exhibitorStandList = exhibitorStandList.filter(e => ![
    () => !filters.programs || filters.programs.includes(e.programId),
    () => !filters.programLabels || (e.programId && programs[e.programId] && programs[e.programId].labels && programs[e.programId].labels.some(l => filters.programLabels.includes(l))),
    () => !filters.exhibitors || filters.exhibitors.includes(e.exhibitorId),
    () => !filters.exhibitorLabels || (exhibitors[e.exhibitorId]?.labels && exhibitors[e.exhibitorId].labels.some(l => filters.exhibitorLabels.includes(l))),
    () => !filters.tags || (exhibitors[e.exhibitorId]?.workforceContactEmail && filters.tags.includes(exhibitors[e.exhibitorId].workforceContactEmail))
  ].some(passesFilterFunction => !passesFilterFunction()))
  const programIds = getSet(exhibitorStandList, 'programId')
  const programList = Object.values(programs).filter(p => programIds.has(p.id))
  const exhibitorIds = getSet(exhibitorStandList, 'exhibitorId')
  const exhibitorList = Object.values(exhibitors).filter(e => exhibitorIds.has(e.id))
  return { exhibitorStandList, exhibitorList, programList }
}

export interface ExhibitorSummary { 
  programName: string
  standSize: Ranking
  standNumber: string
  venueLocation: string
  venueName: string
  venueAddress: string
}

const extractExhibitorProgramSummary = (program: Program, stand: ExhibitorStand, venue: Venue, event: JoiEvent): ExhibitorSummary => ({
  programName: !program ? null : program.title,
  standSize: !event || !event.standTypes ? null : event.standTypes.find(s => s.id === stand.standTypeId),
  standNumber: stand.name,
  venueLocation: !program ? null : program.location,
  venueName: !venue ? null : venue.venue || null,
  venueAddress: !venue ? null : venue.address || null
})

export const extractExhibitorSummaries = (event: JoiEvent, programs: { [pId:string]: Program }, exhibitorId: string): ExhibitorSummary[] => Object.values(event.exhibitorStands)
  .filter(s => s.exhibitorId === exhibitorId)
  .map(s => {
    const program = programs[s.programId]
    const venue = program && program.venueId && event && event.venues ? event.venues[program.venueId] : null
    return extractExhibitorProgramSummary(program, s, venue, event)
  })